import { useCallback, useEffect, useRef, useState } from "react";

import { toast } from "sonner";

import type { SelectableVoice } from "~/features/meditations/types";
import { voiceForAnalytics } from "~/features/meditations/types";
import { trackEvent } from "~/lib/analytics";

interface VoicePreviewResponse {
    url: string;
    expiresAt: string;
}

// AIDEV-NOTE: A silent 0-sample WAV. Playing it synchronously inside the tap "unlocks" the
// shared audio element on strict-autoplay browsers (iOS Safari, Android Chrome); a cold
// preview then spends seconds generating before the real play(), long after the gesture's
// transient activation would have expired (Codex review, 2026-09-09).
const SILENT_WAV_DATA_URI =
    "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAIA+AAACABAAZGF0YQAAAAA=";

function isVoicePreviewResponse(value: unknown): value is VoicePreviewResponse {
    if (typeof value !== "object" || value === null) return false;
    return (
        "url" in value &&
        typeof value.url === "string" &&
        "expiresAt" in value &&
        typeof value.expiresAt === "string"
    );
}

export function useVoicePreview(): {
    playingVoice: SelectableVoice | null;
    loadingVoice: SelectableVoice | null;
    toggle: (voice: SelectableVoice) => Promise<void>;
    stop: () => void;
} {
    const [playingVoice, setPlayingVoice] = useState<SelectableVoice | null>(
        null,
    );
    const [loadingVoice, setLoadingVoice] = useState<SelectableVoice | null>(
        null,
    );
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const requestIdRef = useRef(0);

    const stop = useCallback(() => {
        requestIdRef.current += 1;
        const audio = audioRef.current;
        if (audio) {
            audio.onended = null;
            audio.onerror = null;
            audio.pause();
            audio.removeAttribute("src");
            audio.load();
        }
        setPlayingVoice(null);
        setLoadingVoice(null);
    }, []);

    useEffect(() => {
        return () => {
            requestIdRef.current += 1;
            const audio = audioRef.current;
            if (audio) {
                audio.onended = null;
                audio.onerror = null;
                audio.pause();
                audio.removeAttribute("src");
                audio.load();
            }
            audioRef.current = null;
        };
    }, []);

    const toggle = useCallback(
        async (voice: SelectableVoice) => {
            if (playingVoice === voice || loadingVoice === voice) {
                stop();
                return;
            }

            const requestId = requestIdRef.current + 1;
            requestIdRef.current = requestId;

            const currentAudio = audioRef.current;
            if (currentAudio) {
                currentAudio.onended = null;
                currentAudio.onerror = null;
                currentAudio.pause();
                currentAudio.removeAttribute("src");
                currentAudio.load();
            }
            setPlayingVoice(null);
            setLoadingVoice(voice);

            const audio = audioRef.current ?? new Audio();
            audioRef.current = audio;
            audio.src = SILENT_WAV_DATA_URI;
            void audio.play().catch(() => {
                // The unlock is best-effort; the real play() below reports actual errors.
            });

            try {
                const response = await fetch(
                    `/api/meditation/voice-preview?voice=${encodeURIComponent(
                        voice,
                    )}`,
                );
                if (!response.ok)
                    throw new Error("Voice preview request failed");

                const body: unknown = await response.json();
                if (!isVoicePreviewResponse(body)) {
                    throw new Error("Voice preview response was invalid");
                }

                // AIDEV-NOTE: Ignore a late response after another voice (or Stop) became the active request.
                if (requestIdRef.current !== requestId) return;

                audio.src = body.url;
                audio.onended = () => {
                    if (requestIdRef.current === requestId) {
                        setPlayingVoice(null);
                    }
                };
                audio.onerror = () => {
                    if (requestIdRef.current !== requestId) return;
                    requestIdRef.current += 1;
                    setPlayingVoice(null);
                    setLoadingVoice(null);
                    toast.error("We couldn't play that sample. Try again.");
                };

                await audio.play();
                if (requestIdRef.current !== requestId) {
                    audio.pause();
                    return;
                }

                setLoadingVoice(null);
                setPlayingVoice(voice);
                trackEvent("meditation voice previewed", {
                    voice: voiceForAnalytics(voice),
                });
            } catch {
                if (requestIdRef.current !== requestId) return;
                requestIdRef.current += 1;
                setLoadingVoice(null);
                setPlayingVoice(null);
                toast.error("We couldn't play that sample. Try again.");
            }
        },
        [loadingVoice, playingVoice, stop],
    );

    return { playingVoice, loadingVoice, toggle, stop };
}
